import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/SiteChrome";
import { FileField, GeoField, Stepper } from "@/components/KycFields";
import { api, apiError, dashboardPath, ID_TYPES, NG_STATES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export const OnboardShell = ({ title, subtitle, steps, step, children, onBack, onNext, busy, canNext, submitLabel }) => (
  <div className="min-h-screen bg-sand">
    <div className="mx-auto max-w-2xl px-4 py-10 sm:py-14">
      <Logo />
      <div className="mt-8 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="onboarding-title">{title}</h1>
        <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
        <div className="mt-7">
          <Stepper steps={steps} current={step} />
        </div>
        <div className="mt-8 space-y-5">{children}</div>
        <div className="mt-9 flex gap-3">
          {step > 0 && (
            <Button variant="outline" className="rounded-full px-6" onClick={onBack} data-testid="kyc-back-btn">
              Back
            </Button>
          )}
          <Button
            className="flex-1 rounded-full bg-emerald-900 py-6 hover:bg-emerald-800"
            onClick={onNext}
            disabled={busy || !canNext}
            data-testid="kyc-next-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : step === steps.length - 1 ? submitLabel : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export const Select = ({ value, onChange, options, testId, placeholder = "Select…" }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
    ))}
  </select>
);

export const EscrowConsent = ({ checked, onChange, testId }) => (
  <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
    <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
    <span className="text-sm text-slate-700">
      I accept the <strong>CARENEST Escrow Terms &amp; Conditions</strong> — booking funds are held in escrow and
      released only after care is delivered and confirmed.
    </span>
  </label>
);

export function useKycSubmit(endpoint) {
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const submit = async (payload) => {
    setBusy(true);
    try {
      await api.post(endpoint, payload);
      const user = await refresh();
      toast.success("KYC submitted for review");
      navigate(dashboardPath(user.role), { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, submit };
}

export { ID_TYPES, NG_STATES, Input, Textarea, FileField, GeoField };
